// src/pages/tenant/TenantOnboardingPage.tsx
// Step-by-step onboarding wizard for new tenant setup, built on the form kit
// (docs/ux/14-forms.md): FormRow settings rows, SegmentedControl for small
// enums, AutoGrowTextarea for long text. A wizard is a page flow, not a
// dialog — the vertical stepper stays, each step validates before advancing.
//
// Contract notes (generated types in @core/types/tenant):
// - Step flags are is_required / is_completed / is_skipped (OnboardingStep).
// - UpdateTenantOnboarding(tenant_id, { onboarding }) carries the whole
//   TenantOnboarding; tenant profile fields (name, description, logo) and
//   workspace defaults (timezone, locale) live on Tenant and are saved via
//   UpdateTenant(tenant_id, { tenant }).
// - Team invites have no generated tenant-level email-invite API; pending
//   invites are persisted in onboarding.metadata (Record<string, string>).

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useTenant, useTenantTenant, useProduct, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Badge,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';
import { FormRow, SegmentedControl, AutoGrowTextarea } from '@/components/form';
import type {
  OnboardingStep,
  Tenant,
  TenantBranding,
  TenantOnboarding,
  TenantSettings,
} from '@core/types/tenant';
import type { Product } from '@core/types/product';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** IANA timezone identifiers — data, not translatable copy. */
const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Moscow',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Kolkata',
  'Australia/Sydney',
];

/** Language autonyms — each language named in itself, same in every UI locale. */
const LOCALE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'ru', label: 'Русский' },
  { value: 'de', label: 'Deutsch' },
  { value: 'fr', label: 'Français' },
  { value: 'es', label: 'Español' },
  { value: 'pt', label: 'Português' },
  { value: 'ja', label: '日本語' },
  { value: 'zh', label: '中文' },
];

interface PlanOption {
  id: string;
  nameKey: string;
  price: string;
  highlighted?: boolean;
  featureKeys: string[];
}

const PLAN_OPTIONS: PlanOption[] = [
  {
    id: 'free',
    nameKey: 'tenant.onboarding.planFree',
    price: '$0',
    featureKeys: [
      'tenant.onboarding.planFreeFeature1',
      'tenant.onboarding.planFreeFeature2',
      'tenant.onboarding.planFreeFeature3',
      'tenant.onboarding.planFreeFeature4',
    ],
  },
  {
    id: 'pro',
    nameKey: 'tenant.onboarding.planPro',
    price: '$29',
    highlighted: true,
    featureKeys: [
      'tenant.onboarding.planProFeature1',
      'tenant.onboarding.planProFeature2',
      'tenant.onboarding.planProFeature3',
      'tenant.onboarding.planProFeature4',
      'tenant.onboarding.planProFeature5',
      'tenant.onboarding.planProFeature6',
    ],
  },
  {
    id: 'enterprise',
    nameKey: 'tenant.onboarding.planEnterprise',
    price: '$99',
    featureKeys: [
      'tenant.onboarding.planEnterpriseFeature1',
      'tenant.onboarding.planEnterpriseFeature2',
      'tenant.onboarding.planEnterpriseFeature3',
      'tenant.onboarding.planEnterpriseFeature4',
      'tenant.onboarding.planEnterpriseFeature5',
      'tenant.onboarding.planEnterpriseFeature6',
      'tenant.onboarding.planEnterpriseFeature7',
    ],
  },
];

const INVITE_ROLES = [
  { value: 'admin', labelKey: 'tenant.onboarding.roleAdmin' },
  { value: 'manager', labelKey: 'tenant.onboarding.roleManager' },
  { value: 'member', labelKey: 'tenant.onboarding.roleMember' },
  { value: 'viewer', labelKey: 'tenant.onboarding.roleViewer' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INPUT_CLASS =
  'w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring';

interface InviteRow {
  email: string;
  role: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type StepStatus = 'completed' | 'current' | 'skipped' | 'upcoming';

function stepStatus(step: OnboardingStep, currentIndex: number, stepIndex: number): StepStatus {
  if (step.is_completed) return 'completed';
  if (step.is_skipped) return 'skipped';
  if (stepIndex === currentIndex) return 'current';
  return 'upcoming';
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

/** Status icon for the vertical stepper. */
function StepIcon({ status, order }: { status: StepStatus; order: number }) {
  if (status === 'completed') {
    return (
      <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
        <CheckIcon className="w-4 h-4 text-white" />
      </div>
    );
  }
  if (status === 'skipped') {
    return (
      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
        <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l4-4 4 4M7 4v16" />
        </svg>
      </div>
    );
  }
  if (status === 'current') {
    return (
      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0 ring-4 ring-primary/20">
        <span className="text-sm font-semibold text-primary-foreground">{order}</span>
      </div>
    );
  }
  return (
    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
      <span className="text-sm font-semibold text-muted-foreground">{order}</span>
    </div>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="mt-1 text-xs text-destructive">{message}</p>;
}

// ---------------------------------------------------------------------------
// Step content components
// ---------------------------------------------------------------------------

interface ProfileData {
  name: string;
  description: string;
  logoUrl: string;
}

function ProfileStepContent({
  data,
  errors,
  onChange,
}: {
  data: ProfileData;
  errors: Record<string, string>;
  onChange: (patch: Partial<ProfileData>) => void;
}) {
  const { t } = useI18n();
  return (
    <div>
      <FormRow label={t('tenant.onboarding.tenantName')} htmlFor="onb-tenant-name">
        <input
          id="onb-tenant-name"
          type="text"
          value={data.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder={t('tenant.onboarding.tenantNamePlaceholder')}
          autoCapitalize="off"
          className={INPUT_CLASS}
        />
        <FieldError message={errors.name} />
      </FormRow>
      <FormRow label={t('tenant.onboarding.description')} htmlFor="onb-tenant-description">
        <AutoGrowTextarea
          id="onb-tenant-description"
          value={data.description}
          onChange={(description) => onChange({ description })}
          placeholder={t('tenant.onboarding.descriptionPlaceholder')}
          minRows={2}
          className="max-w-md rounded-md border border-input bg-background px-3 py-2"
        />
      </FormRow>
      <FormRow label={t('tenant.onboarding.logo')} hint={t('tenant.onboarding.logoHint')} htmlFor="onb-logo-url">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-lg bg-muted border border-dashed border-border flex items-center justify-center overflow-hidden flex-shrink-0">
            {data.logoUrl ? (
              <img src={data.logoUrl} alt={t('tenant.onboarding.logo')} className="w-full h-full object-cover" />
            ) : (
              <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z"
                />
              </svg>
            )}
          </div>
          <input
            id="onb-logo-url"
            type="text"
            value={data.logoUrl}
            onChange={(e) => onChange({ logoUrl: e.target.value })}
            placeholder={t('tenant.onboarding.logoUrlPlaceholder')}
            aria-label={t('tenant.onboarding.logoUrl')}
            autoCapitalize="off"
            className={`${INPUT_CLASS} font-mono`}
          />
        </div>
      </FormRow>
    </div>
  );
}

function InviteTeamStepContent({
  invites,
  errors,
  onAdd,
  onRemove,
  onChange,
}: {
  invites: InviteRow[];
  errors: Record<string, string>;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onChange: (index: number, patch: Partial<InviteRow>) => void;
}) {
  const { t } = useI18n();
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('tenant.onboarding.inviteTeamDescription')}</p>
      <div className="space-y-3">
        {invites.map((invite, idx) => (
          <div key={idx}>
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="email"
                value={invite.email}
                onChange={(e) => onChange(idx, { email: e.target.value })}
                placeholder={t('tenant.onboarding.emailPlaceholder')}
                aria-label={t('tenant.onboarding.emailPlaceholder')}
                autoCapitalize="off"
                className={`${INPUT_CLASS} flex-1 min-w-[14rem]`}
              />
              <SegmentedControl
                aria-label={t('tenant.onboarding.inviteRole')}
                size="sm"
                value={invite.role}
                onChange={(role) => onChange(idx, { role })}
                options={INVITE_ROLES.map((r) => ({ value: r.value, label: t(r.labelKey) }))}
              />
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive flex-shrink-0"
                onClick={() => onRemove(idx)}
                disabled={invites.length <= 1}
                aria-label={t('common.remove')}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Button>
            </div>
            <FieldError message={errors[`invite.${idx}`]} />
          </div>
        ))}
      </div>
      <Button variant="secondary" size="sm" onClick={onAdd}>
        {t('tenant.onboarding.addAnother')}
      </Button>
    </div>
  );
}

function SelectProductsStepContent({
  products,
  loading,
  selected,
  error,
  onToggle,
}: {
  products: Product[];
  loading: boolean;
  selected: Set<string>;
  error?: string;
  onToggle: (id: string) => void;
}) {
  const { t } = useI18n();
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('tenant.onboarding.selectProductsDescription')}</p>
      {loading ? (
        <div className="py-8 text-center text-muted-foreground text-sm">{t('common.loading')}</div>
      ) : products.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground text-sm">
          {t('tenant.onboarding.noProductsAvailable')}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((product) => {
            const productId = product.entity?.id ?? '';
            const isSelected = selected.has(productId);
            const colorPrimary = product.color_primary || '#6366f1';
            return (
              <button
                key={productId || product.slug}
                type="button"
                onClick={() => onToggle(productId)}
                aria-pressed={isSelected}
                className={`flex flex-col items-start rounded-xl border-2 p-4 text-left transition-all ${
                  isSelected ? 'border-primary bg-primary/5 shadow-sm' : 'border-border hover:border-primary/40'
                }`}
              >
                <div className="flex items-center gap-3 w-full">
                  {product.icon_url ? (
                    <img src={product.icon_url} alt={product.name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-white font-bold text-sm"
                      style={{ backgroundColor: colorPrimary }}
                      aria-hidden="true"
                    >
                      {product.name.charAt(0)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground truncate">{product.name}</p>
                    {product.tagline && <p className="text-xs text-muted-foreground truncate">{product.tagline}</p>}
                  </div>
                  {isSelected && (
                    <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                      <CheckIcon className="w-3 h-3 text-primary-foreground" />
                    </div>
                  )}
                </div>
                {product.description && (
                  <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{product.description}</p>
                )}
              </button>
            );
          })}
        </div>
      )}
      <FieldError message={error} />
    </div>
  );
}

function SelectPlanStepContent({
  selectedPlan,
  referralCode,
  errors,
  onSelect,
  onReferralChange,
}: {
  selectedPlan: string;
  referralCode: string;
  errors: Record<string, string>;
  onSelect: (id: string) => void;
  onReferralChange: (code: string) => void;
}) {
  const { t } = useI18n();
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('tenant.onboarding.selectPlanDescription')}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {PLAN_OPTIONS.map((plan) => {
          const isSelected = selectedPlan === plan.id;
          return (
            <button
              key={plan.id}
              type="button"
              onClick={() => onSelect(plan.id)}
              aria-pressed={isSelected}
              className={`relative flex flex-col rounded-xl border-2 p-5 text-left transition-all ${
                isSelected ? 'border-primary bg-primary/5 shadow-sm' : 'border-border hover:border-primary/40'
              }`}
            >
              {plan.highlighted && (
                <Badge variant="primary" size="sm" className="absolute -top-2.5 left-4">
                  {t('tenant.onboarding.popular')}
                </Badge>
              )}
              <h3 className="text-lg font-semibold text-foreground">{t(plan.nameKey)}</h3>
              <div className="mt-2 flex items-baseline gap-0.5">
                <span className="text-2xl font-bold text-foreground">{plan.price}</span>
                <span className="text-sm text-muted-foreground">{t('tenant.onboarding.perMonth')}</span>
              </div>
              <ul className="mt-4 space-y-2 flex-1">
                {plan.featureKeys.map((featureKey) => (
                  <li key={featureKey} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
                    {t(featureKey)}
                  </li>
                ))}
              </ul>
              {isSelected && (
                <div className="mt-4 flex items-center justify-center">
                  <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                    <CheckIcon className="w-3 h-3 text-primary-foreground" />
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>
      <FieldError message={errors.plan} />
      <FormRow
        label={t('tenant.onboarding.referralCode')}
        hint={t('tenant.onboarding.referralCodeHint')}
        htmlFor="onb-referral-code"
      >
        <input
          id="onb-referral-code"
          type="text"
          value={referralCode}
          onChange={(e) => onReferralChange(e.target.value)}
          placeholder={t('tenant.onboarding.referralCodePlaceholder')}
          autoCapitalize="off"
          className={`${INPUT_CLASS} max-w-xs font-mono`}
        />
      </FormRow>
    </div>
  );
}

interface ConfigData {
  timezone: string;
  locale: string;
}

function ConfigureStepContent({
  data,
  onChange,
}: {
  data: ConfigData;
  onChange: (patch: Partial<ConfigData>) => void;
}) {
  const { t } = useI18n();
  return (
    <div>
      <p className="text-sm text-muted-foreground mb-2">{t('tenant.onboarding.configureDescription')}</p>
      <FormRow label={t('tenant.onboarding.timezone')}>
        <div className="w-64">
          <Select
            value={data.timezone}
            onChange={(e) => onChange({ timezone: e.target.value })}
            aria-label={t('tenant.onboarding.timezone')}
            options={TIMEZONES.map((tz) => ({ value: tz, label: tz }))}
          />
        </div>
      </FormRow>
      <FormRow label={t('tenant.onboarding.locale')}>
        <div className="w-64">
          <Select
            value={data.locale}
            onChange={(e) => onChange({ locale: e.target.value })}
            aria-label={t('tenant.onboarding.locale')}
            options={LOCALE_OPTIONS}
          />
        </div>
      </FormRow>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * TenantOnboardingPage - step-by-step wizard for new tenant setup.
 * Route: /tenants/:id/onboarding
 */
export const TenantOnboardingPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useTenant();
  const tenantCrud = useTenantTenant();
  const productStore = useProduct();
  const { t } = useI18n();

  const [onboarding, setOnboarding] = useState<TenantOnboarding | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);

  const [activeStep, setActiveStep] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Step-specific form data
  const [profile, setProfile] = useState<ProfileData>({ name: '', description: '', logoUrl: '' });
  const [invites, setInvites] = useState<InviteRow[]>([{ email: '', role: 'member' }]);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [selectedPlan, setSelectedPlan] = useState('pro');
  const [referralCode, setReferralCode] = useState('');
  const [config, setConfig] = useState<ConfigData>({ timezone: 'UTC', locale: 'en' });

  const [submitting, setSubmitting] = useState(false);
  const [completing, setCompleting] = useState(false);

  const steps = useMemo(
    () => [...(onboarding?.steps ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [onboarding],
  );

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);

    const loadOnboarding = store.GetTenantOnboarding(id).then((resp) => {
      if (cancelled) return;
      const data = resp?.onboarding;
      if (!data) return;
      setOnboarding(data);

      const ordered = [...(data.steps ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const firstIncomplete = ordered.findIndex((s) => !s.is_completed && !s.is_skipped);
      if (firstIncomplete >= 0) setActiveStep(firstIncomplete);

      if (data.selected_products?.length) setSelectedProducts(new Set(data.selected_products));
      if (data.selected_plan) setSelectedPlan(data.selected_plan);
      if (data.referral_code) setReferralCode(data.referral_code);

      const pending = data.metadata?.pending_invites;
      if (pending) {
        try {
          const parsed = JSON.parse(pending) as InviteRow[];
          if (Array.isArray(parsed) && parsed.length > 0) {
            setInvites(parsed.map((p) => ({ email: p.email ?? '', role: p.role ?? 'member' })));
          }
        } catch {
          // Ignore malformed metadata
        }
      }
    });

    const loadTenant = tenantCrud
      .GetTenant(id)
      .then((loaded) => {
        if (cancelled || !loaded) return;
        setTenant(loaded);
        setProfile({
          name: loaded.name ?? '',
          description: loaded.description ?? '',
          logoUrl: loaded.branding?.logo_url ?? '',
        });
        setConfig({
          timezone: loaded.settings?.default_timezone || 'UTC',
          locale: loaded.settings?.default_locale || 'en',
        });
      })
      .catch(() => {
        // Tenant prefill is best-effort; onboarding still renders
      });

    Promise.allSettled([loadOnboarding, loadTenant]).then(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const hasProductsStep = steps.some((s) => s.step_id === 'select_products');
  useEffect(() => {
    if (!hasProductsStep || productStore.items.length > 0) return;
    productStore.ListProducts().catch(() => {
      // Error surfaced via product store
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasProductsStep]);

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------

  const clearFieldError = (key: string) => {
    setFieldErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validateStep = (step: OnboardingStep): boolean => {
    const errors: Record<string, string> = {};
    switch (step.step_id) {
      case 'profile':
        if (!profile.name.trim()) errors.name = t('tenant.onboarding.errorNameRequired');
        break;
      case 'invite_team':
        invites.forEach((inv, idx) => {
          if (inv.email.trim() && !EMAIL_RE.test(inv.email.trim())) {
            errors[`invite.${idx}`] = t('tenant.onboarding.errorEmailInvalid');
          }
        });
        break;
      case 'select_products':
        if (step.is_required && selectedProducts.size === 0) {
          errors.products = t('tenant.onboarding.errorProductRequired');
        }
        break;
      case 'select_plan':
        if (!selectedPlan) errors.plan = t('tenant.onboarding.errorPlanRequired');
        break;
      default:
        break;
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ---------------------------------------------------------------------------
  // Persistence
  // ---------------------------------------------------------------------------

  /** Save profile / configure fields onto the Tenant itself (their real home). */
  const saveTenantFields = async (stepId: 'profile' | 'configure') => {
    if (!id) return;
    const base: Tenant = tenant ?? ({ name: '', slug: '', description: '', region: '' } as Tenant);
    let updated: Tenant;
    if (stepId === 'profile') {
      const branding: TenantBranding = {
        logo_url: '',
        favicon_url: '',
        primary_color: '',
        secondary_color: '',
        custom_domain: '',
        support_email: '',
        support_url: '',
        terms_url: '',
        privacy_url: '',
        ...(base.branding ?? {}),
      };
      branding.logo_url = profile.logoUrl.trim();
      updated = {
        ...base,
        name: profile.name.trim(),
        description: profile.description.trim(),
        branding,
      };
    } else {
      const settings = {
        ...(base.settings ?? {}),
        default_timezone: config.timezone,
        default_locale: config.locale,
      } as TenantSettings;
      updated = { ...base, settings };
    }
    const saved = await tenantCrud.UpdateTenant(id, { tenant: updated });
    setTenant(saved ?? updated);
  };

  /** Build the next TenantOnboarding for UpdateTenantOnboarding({ onboarding }). */
  const buildUpdatedOnboarding = (
    stepIndex: number,
    action: 'complete' | 'skip',
  ): TenantOnboarding | null => {
    if (!onboarding) return null;
    const nextSteps = steps.map((s, i) =>
      i === stepIndex
        ? { ...s, is_completed: action === 'complete', is_skipped: action === 'skip' }
        : s,
    );
    const completedCount = nextSteps.filter((s) => s.is_completed).length;
    const completionPercent = nextSteps.length
      ? Math.round((completedCount / nextSteps.length) * 100)
      : 0;
    const nextIncomplete =
      nextSteps.find((s, i) => i > stepIndex && !s.is_completed && !s.is_skipped) ??
      nextSteps.find((s) => !s.is_completed && !s.is_skipped);

    const pendingInvites = invites.filter((inv) => inv.email.trim());
    const metadata =
      pendingInvites.length > 0
        ? { ...(onboarding.metadata ?? {}), pending_invites: JSON.stringify(pendingInvites) }
        : onboarding.metadata;

    return {
      ...onboarding,
      status: 'TENANT_ONBOARDING_STATUS_IN_PROGRESS',
      steps: nextSteps,
      completion_percent: completionPercent,
      current_step_id: nextIncomplete?.step_id ?? '',
      selected_products: Array.from(selectedProducts),
      selected_plan: selectedPlan || onboarding.selected_plan,
      referral_code: referralCode.trim() || onboarding.referral_code,
      metadata,
    };
  };

  /**
   * Apply the server response but keep locally-updated steps/products/metadata:
   * the backend merge only persists status, completion_percent, current_step_id,
   * selected_plan, referral_code and completed_at.
   */
  const applyOnboarding = (local: TenantOnboarding, server?: TenantOnboarding) => {
    setOnboarding({
      ...(server ?? local),
      steps: local.steps,
      selected_products: local.selected_products,
      metadata: local.metadata,
    });
  };

  const advanceFrom = (stepIndex: number, nextSteps: OnboardingStep[]) => {
    const nextIncomplete = nextSteps.findIndex(
      (s, idx) => idx > stepIndex && !s.is_completed && !s.is_skipped,
    );
    if (nextIncomplete >= 0) setActiveStep(nextIncomplete);
  };

  const handleCompleteStep = async (stepIndex: number) => {
    if (!id || !onboarding) return;
    const step = steps[stepIndex];
    if (!step || !validateStep(step)) return;

    setSubmitting(true);
    try {
      if (step.step_id === 'profile' || step.step_id === 'configure') {
        await saveTenantFields(step.step_id);
      }
      const next = buildUpdatedOnboarding(stepIndex, 'complete');
      if (!next) return;
      const resp = await store.UpdateTenantOnboarding(id, { onboarding: next });
      applyOnboarding(next, resp?.onboarding);
      advanceFrom(stepIndex, next.steps);
    } catch {
      // Error surfaced via store.error
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkipStep = async (stepIndex: number) => {
    if (!id || !onboarding) return;
    const step = steps[stepIndex];
    if (!step || step.is_required) return;

    setSubmitting(true);
    try {
      const next = buildUpdatedOnboarding(stepIndex, 'skip');
      if (!next) return;
      const resp = await store.UpdateTenantOnboarding(id, { onboarding: next });
      applyOnboarding(next, resp?.onboarding);
      advanceFrom(stepIndex, next.steps);
    } catch {
      // Error surfaced via store.error
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteOnboarding = async () => {
    if (!id) return;
    setCompleting(true);
    try {
      await store.CompleteTenantOnboarding(id, {});
      navigate(`/tenants/${id}`);
    } catch {
      // Error surfaced via store.error
    } finally {
      setCompleting(false);
    }
  };

  // Invite helpers
  const handleAddInvite = () => setInvites((prev) => [...prev, { email: '', role: 'member' }]);

  const handleRemoveInvite = (index: number) => {
    if (invites.length <= 1) return;
    setInvites((prev) => prev.filter((_, i) => i !== index));
    clearFieldError(`invite.${index}`);
  };

  const handleInviteChange = (index: number, patch: Partial<InviteRow>) => {
    setInvites((prev) => prev.map((inv, i) => (i === index ? { ...inv, ...patch } : inv)));
    if (patch.email !== undefined) clearFieldError(`invite.${index}`);
  };

  const handleToggleProduct = (productId: string) => {
    if (!productId) return;
    setSelectedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
    clearFieldError('products');
  };

  const handleProfileChange = (patch: Partial<ProfileData>) => {
    setProfile((prev) => ({ ...prev, ...patch }));
    if (patch.name !== undefined) clearFieldError('name');
  };

  const handleSelectPlan = (planId: string) => {
    setSelectedPlan(planId);
    clearFieldError('plan');
  };

  const completionPercent = onboarding?.completion_percent ?? 0;
  const tenantName = tenant?.name || profile.name || '';
  const allRequiredDone = steps.filter((s) => s.is_required).every((s) => s.is_completed);
  const completedCount = steps.filter((s) => s.is_completed).length;

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (store.error && !onboarding) {
    return (
      <div className="max-w-4xl mx-auto space-y-3">
        <ErrorAlert error={store.error} />
        <Link to="/tenants" className="inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/tenants" className="text-muted-foreground hover:text-foreground">
          {t('tenant.list')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        {tenantName ? (
          <>
            <Link to={`/tenants/${id}`} className="text-muted-foreground hover:text-foreground">
              {tenantName}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
          </>
        ) : null}
        <span className="text-foreground">{t('tenant.onboarding.setup')}</span>
      </nav>

      {/* The tab already names this page; the heading stays for screen readers. */}
      <h1 className="sr-only">{t('tenant.onboarding.title')}</h1>

      {/* Error */}
      {store.error ? (
        <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />
      ) : null}

      {/* Progress bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">{t('tenant.onboarding.progress')}</span>
            <span className="text-sm font-semibold text-foreground">{completionPercent}%</span>
          </div>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-500"
              style={{ width: `${completionPercent}%` }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Vertical stepper + content */}
      <div className="space-y-4">
        {steps.map((step, idx) => {
          const status = stepStatus(step, activeStep, idx);
          const isActive = idx === activeStep;
          const order = step.order || idx + 1;

          return (
            <div key={step.step_id || idx} className="relative">
              {/* Connector line */}
              {idx < steps.length - 1 && (
                <div
                  className={`absolute left-4 top-10 w-0.5 h-[calc(100%-2rem)] ${
                    step.is_completed ? 'bg-green-500' : 'bg-border'
                  }`}
                />
              )}

              <Card className={`transition-all ${isActive ? 'ring-2 ring-primary/20' : ''}`}>
                {/* Step header (always visible) */}
                <button type="button" onClick={() => setActiveStep(idx)} className="w-full text-left">
                  <CardHeader className="pb-0">
                    <div className="flex items-start gap-4">
                      <StepIcon status={status} order={order} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-base">{step.title || step.step_id}</CardTitle>
                          {step.is_completed && (
                            <Badge variant="success" size="sm">
                              {t('tenant.onboarding.completed')}
                            </Badge>
                          )}
                          {step.is_skipped && (
                            <Badge variant="secondary" size="sm">
                              {t('tenant.onboarding.skipped')}
                            </Badge>
                          )}
                          {step.is_required && !step.is_completed && !step.is_skipped && (
                            <Badge variant="warning" size="sm">
                              {t('tenant.onboarding.required')}
                            </Badge>
                          )}
                        </div>
                        <CardDescription className="mt-1">{step.description || ''}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </button>

                {/* Step content (expanded when active) */}
                {isActive && (
                  <>
                    <CardContent className="pt-4 pl-16">
                      {step.step_id === 'profile' && (
                        <ProfileStepContent data={profile} errors={fieldErrors} onChange={handleProfileChange} />
                      )}
                      {step.step_id === 'invite_team' && (
                        <InviteTeamStepContent
                          invites={invites}
                          errors={fieldErrors}
                          onAdd={handleAddInvite}
                          onRemove={handleRemoveInvite}
                          onChange={handleInviteChange}
                        />
                      )}
                      {step.step_id === 'select_products' && (
                        <SelectProductsStepContent
                          products={productStore.items}
                          loading={productStore.isLoading}
                          selected={selectedProducts}
                          error={fieldErrors.products}
                          onToggle={handleToggleProduct}
                        />
                      )}
                      {step.step_id === 'select_plan' && (
                        <SelectPlanStepContent
                          selectedPlan={selectedPlan}
                          referralCode={referralCode}
                          errors={fieldErrors}
                          onSelect={handleSelectPlan}
                          onReferralChange={setReferralCode}
                        />
                      )}
                      {step.step_id === 'configure' && (
                        <ConfigureStepContent
                          data={config}
                          onChange={(patch) => setConfig((prev) => ({ ...prev, ...patch }))}
                        />
                      )}
                    </CardContent>
                    <CardFooter className="pl-16 flex items-center gap-3">
                      {idx > 0 && (
                        <Button variant="ghost" onClick={() => setActiveStep(idx - 1)} disabled={submitting}>
                          {t('common.back')}
                        </Button>
                      )}
                      {!step.is_completed && !step.is_skipped && (
                        <Button onClick={() => handleCompleteStep(idx)} disabled={submitting}>
                          {submitting ? t('common.loading') : t('tenant.onboarding.completeStep')}
                        </Button>
                      )}
                      {!step.is_required && !step.is_completed && !step.is_skipped && (
                        <Button variant="ghost" onClick={() => handleSkipStep(idx)} disabled={submitting}>
                          {t('tenant.onboarding.skip')}
                        </Button>
                      )}
                      {(step.is_completed || step.is_skipped) && (
                        <span className="text-sm text-muted-foreground">
                          {step.is_completed
                            ? t('tenant.onboarding.stepDone')
                            : t('tenant.onboarding.stepSkipped')}
                        </span>
                      )}
                    </CardFooter>
                  </>
                )}
              </Card>
            </div>
          );
        })}
      </div>

      {/* Complete onboarding */}
      {steps.length > 0 && (
        <Card>
          <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-foreground">
                {allRequiredDone
                  ? t('tenant.onboarding.readyToComplete')
                  : t('tenant.onboarding.completeRequired')}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t('tenant.onboarding.stepsCompleted', { count: completedCount, total: steps.length })}
              </p>
            </div>
            <Button
              disabled={!allRequiredDone || completing}
              onClick={handleCompleteOnboarding}
              className="min-w-[180px]"
            >
              {completing ? t('common.loading') : t('tenant.onboarding.completeOnboarding')}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
});

export default TenantOnboardingPage;
